import { EncryptionService } from "@marketplace/shared/services";
import { captureException } from "@marketplace/shared/utils";

/**
 * Configuration options for SecureStorage
 */
export interface SecureStorageConfig {
  /** The secret key used for encryption/decryption */
  secretKey: string;
  /** Optional custom error handler (defaults to Sentry's captureException) */
  onError?: (error: unknown) => void;
}

/**
 * A class for securely storing and retrieving encrypted data in localStorage.
 * Uses AES encryption to protect sensitive data at rest.
 *
 * @example
 * ```typescript
 * const storage = new SecureStorage({
 *   secretKey: 'your-secret-key'
 * });
 *
 * // Store data
 * await storage.set('userData', { name: 'John' });
 *
 * // Retrieve data
 * const userData = await storage.get<{ name: string }>('userData');
 * ```
 */
export class SecureStorage {
  private readonly encryptionService: EncryptionService;
  private readonly onError: (error: unknown) => void;

  /**
   * Creates a new SecureStorage instance
   * @param config Configuration options
   * @throws Error if secretKey is not provided
   */
  constructor({ secretKey, onError = captureException }: SecureStorageConfig) {
    if (!secretKey) {
      throw new Error("A secret key is required.");
    }
    this.onError = onError;
    this.encryptionService = new EncryptionService({
      secretKey,
      onError
    });
  }

  /**
   * Securely stores data in localStorage
   * @param key The key to store the data under
   * @param data The data to store (will be encrypted)
   * @throws Error if storage or encryption fails
   */
  public async set(key: string, data: unknown): Promise<void> {
    try {
      const encryptedData = await this.encryptionService.encrypt(data);
      localStorage.setItem(key, encryptedData);
    } catch (error) {
      this.onError(new Error(`Failed to save data to local storage: ${error}`));
      throw new Error("Failed to securely store data", { cause: error });
    }
  }

  /**
   * Retrieves and decrypts data from localStorage
   * @param key The key to retrieve the data from
   * @returns The decrypted data or null if not found
   * @template T The expected type of the decrypted data
   */
  public async get<T>(key: string): Promise<T | null> {
    const encryptedData = localStorage.getItem(key);
    if (!encryptedData) return null;

    try {
      return await this.encryptionService.decrypt<T>(encryptedData);
    } catch (error) {
      this.onError(new Error(`Failed to retrieve or decrypt data: ${error}`));
      return null;
    }
  }

  /**
   * Removes data from localStorage
   * @param key The key of the data to remove
   * @throws Error if removal fails
   */
  public remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      this.onError(
        new Error(`Failed to remove data from local storage: ${error}`)
      );
      throw new Error("Failed to remove data", { cause: error });
    }
  }

  /**
   * Clears all data from localStorage
   * @throws Error if clearing fails
   */
  public clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      this.onError(new Error(`Failed to clear local storage: ${error}`));
      throw new Error("Failed to clear storage", { cause: error });
    }
  }
}
